import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';

// Pages
import Login from './pages/login';
import Register from './pages/register';
import Dashboard from './pages/Dashboard';
import UploadEmails from './pages/UploadEmails';
import ComposeEmail from './pages/ComposeEmail';
import EmailSettings from './pages/EmailSettings';
import AdminPage from './pages/AdminPage';
import PaymentPage from './pages/PaymentPage';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      setLoading(false);

      if (currentUser) {
        try {
          const token = await currentUser.getIdToken();
          const response = await fetch(`${API_BASE}/api/auth/verify-token`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ token }),
          });

          const data = await response.json();
          if (data.success && data.uid) {
            localStorage.setItem('userId', data.uid);
          } else {
            localStorage.removeItem('userId');
          }
        } catch (err) {
          localStorage.removeItem('userId');
        }
      } else {
        localStorage.removeItem('userId');
      }
    });

    return () => unsubscribe();
  }, []);

  const ProtectedRoute = ({ children }) => {
    const [checkingStatus, setCheckingStatus] = useState(true);
    const [accessAllowed, setAccessAllowed] = useState(false);

    useEffect(() => {
      const checkAccess = async () => {
        if (!user) {
          setAccessAllowed(false);
          setCheckingStatus(false);
          return;
        }

        try {
          const token = await user.getIdToken();
          const res = await fetch(`${API_BASE}/api/auth/verify-token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token }),
          });

          const data = await res.json();

          if (data.success && data.status === 'active') {
            if (user.email === 'admin@arrow.com' || data.payment_status === 'paid') {
              setAccessAllowed(true);
            } else {
              setAccessAllowed(false);
            }
          } else {
            setAccessAllowed(false);
          }
        } catch {
          setAccessAllowed(false);
        } finally {
          setCheckingStatus(false);
        }
      };

      checkAccess();
    }, [user]);

    if (loading || checkingStatus) return <div className="flex items-center justify-center h-screen">Loading...</div>;

    if (!user) return <Navigate to="/login" />;

    if (!accessAllowed && window.location.pathname !== '/payment') {
      return <Navigate to="/payment" />;
    }

    return children;
  };

  const AdminOnlyRoute = ({ children }) => {
    if (loading) return <div>Loading...</div>;
    if (!user || user.email !== 'admin@arrow.com') return <Navigate to="/dashboard" />;
    return children;
  };

  return (
    <div className="App">
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/upload-emails" element={<ProtectedRoute><UploadEmails /></ProtectedRoute>} />
        <Route path="/compose-email" element={<ProtectedRoute><ComposeEmail /></ProtectedRoute>} />
        <Route path="/email-settings" element={<ProtectedRoute><EmailSettings /></ProtectedRoute>} />
        <Route path="/admin" element={<AdminOnlyRoute><AdminPage /></AdminOnlyRoute>} />
        <Route path="/payment" element={<PaymentPage />} />
        <Route path="*" element={<Navigate to={user ? "/dashboard" : "/login"} />} />
      </Routes>
    </div>
  );
}

export default App;
